import type { AppNotification } from "../../domain/app-types.ts";
import type { StoredEntity } from "../../infrastructure/storage/indexed-db.ts";
import { openNotificationDialog } from "./controller.ts";
import { createNotificationRefreshController } from "./refresh-controller.ts";

export interface NotificationWorkflowCapabilities {
  hasUser(): boolean;
  activeRoot(): string | null;
  isTaskOnlyRoot(rootId: string): boolean;
  notifications(): AppNotification[];
  setNotifications(items: AppNotification[]): void;
  notified(): Set<string>;
  request(path: string, init?: RequestInit): Promise<Response>;
  permission(): NotificationPermission | "unsupported";
  notify(title: string, options: NotificationOptions): void;
  shellPresent(): boolean;
  renderShell(preservePanel?: boolean): Promise<void>;
  delivery(): { email: boolean; webPush: boolean };
  reminderOffsets: readonly number[];
  reminderLabel(offset: number): string;
  setReminderDefaults(rootId: string, offsets: number[]): Promise<void>;
  entities(): Promise<StoredEntity[]>;
  openTask(entity: StoredEntity, id: string, commentId: string | null): Promise<void>;
  navigate(url: string): void;
  icon(name: string): string;
  toast(message: string, error?: boolean): void;
}

export function createNotificationWorkflow(capabilities: NotificationWorkflowCapabilities) {
  const refreshState = createNotificationRefreshController({
    hasUser: capabilities.hasUser,
    request: capabilities.request,
    setNotifications: capabilities.setNotifications,
    notified: capabilities.notified,
    permission: capabilities.permission,
    notify: capabilities.notify,
    shellPresent: capabilities.shellPresent,
    render: () => capabilities.renderShell(false),
  });

  async function refresh(render = true): Promise<void> {
    await refreshState(render);
  }

  async function open(): Promise<void> {
    await openNotificationDialog({
      activeRoot: capabilities.activeRoot,
      isTaskOnlyRoot: capabilities.isTaskOnlyRoot,
      notifications: capabilities.notifications,
      setNotifications: capabilities.setNotifications,
      refresh: () => refresh(false),
      request: capabilities.request,
      delivery: capabilities.delivery(),
      reminderOffsets: capabilities.reminderOffsets,
      reminderLabel: capabilities.reminderLabel,
      setReminderDefaults: capabilities.setReminderDefaults,
      entities: capabilities.entities,
      openTask: capabilities.openTask,
      navigate: capabilities.navigate,
      icon: capabilities.icon,
      toast: capabilities.toast,
      renderShell: capabilities.renderShell,
    });
  }

  return { refresh, open };
}
